import React from "react";
import { Box, Container, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useIntl } from "react-intl";
import { Mail, Linkedin, Facebook, Phone } from "lucide-react";

const shareWindow = (url) => {
  window.open(url, "_blank", "noopener,noreferrer,width=650,height=560");
};

const ShareButtons = ({ shareUrl, title }) => {
  const intl = useIntl();
  const shareLinks = [
    {
      label: "Email",
      icon: Mail,
      onClick: () => {
        window.location.href = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareUrl)}`;
      },
    },
    {
      label: "LinkedIn",
      icon: Linkedin,
      onClick: () => {
        shareWindow(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`);
      },
    },
    {
      label: "Facebook",
      icon: Facebook,
      onClick: () => {
        shareWindow(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`);
      },
    },
    {
      label: "WhatsApp",
      icon: Phone,
      onClick: () => {
        shareWindow(`https://wa.me/?text=${encodeURIComponent(`${title} ${shareUrl}`)}`);
      },
    },
  ];

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {shareLinks.map(({ icon: Icon, label, onClick }) => (
        <Box
          key={label}
          component="button"
          onClick={onClick}
          aria-label={intl.formatMessage({ id: "blog.share_on" }, { platform: label })}
          sx={{
            width: 36,
            height: 36,
            borderRadius: "999px",
            backgroundColor: "hsl(140 8% 93%)",
            border: "1px solid hsl(215 20% 65%)",
            color: "hsl(215.4 16.3% 46.9%)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            transition: "all 0.2s ease",
            "&:hover": {
              color: "hsl(152 60% 32%)",
              borderColor: "hsl(152 60% 32%)",
            },
          }}
        >
          <Icon size={18} strokeWidth={1.5} />
        </Box>
      ))}
    </Box>
  );
};

const BlogHero = ({ title, imageUrl, dateLabel, author, authorLogo, shareUrl }) => {
  return (
    <Box sx={{ backgroundColor: "hsl(140 8% 93%)" }}>
      <Container maxWidth={false} sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3, md: 4 }, py: { xs: 8, lg: 12 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: { xs: 5, lg: 8 }, alignItems: "center" }}>
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: "easeOut" }}
            sx={{ display: "flex", flexDirection: "column", gap: 3 }}
          >
            <Typography
              component="h1"
              sx={{
                fontSize: { md: "2.25rem", lg: "3rem" },
                lineHeight: 1.1,
                color: "hsl(150 30% 14%)",
                textWrap: "balance",
                fontFamily: "Segoe UI Bold, system-ui, sans-serif",
              }}
            >
              {title}
            </Typography>

            <Box sx={{ pt: 2, borderTop: "1px solid", borderColor: "divider", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Box
                  component="img"
                  src={authorLogo}
                  alt={author}
                  sx={{ width: 40, height: 40, borderRadius: "999px", backgroundColor: "hsl(140 8% 93%)", objectFit: "contain", p: 0.5 }}
                />
                <Box>
                  <Typography sx={{ fontSize: 14, fontWeight: 700, color: "hsl(150 30% 14%)" }}>{author}</Typography>
                  <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{dateLabel}</Typography>
                </Box>
              </Box>

              <ShareButtons shareUrl={shareUrl} title={title} />
            </Box>
          </Box>

          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            sx={{ borderRadius: 3, overflow: "hidden", boxShadow: "0 12px 40px -12px hsl(150 30% 14% / 0.15)", aspectRatio: "4 / 3" }}
          >
            <Box component="img" src={imageUrl} alt={title} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default BlogHero;
